// src/pages/DepartmentManagement.tsx
import React, { useState, useEffect } from 'react';
import { Plus, Search } from 'lucide-react';
import { DepartmentForm } from '../components/DepartmentForm';
import { DepartmentList } from '../components/DepartmentList';
import departmentApi from '../api/departmentApi';
import { Department } from '../types/department';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { Popup } from '../components/Popup';

export function DepartmentManagement() {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { showError, showSuccess, popup, closePopup } = usePermissionGuard();

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const response = await departmentApi.getDepartments();
      setDepartments(response.data || response);
    } catch (err: any) {
      console.error('Failed to fetch departments:', err);
      showError('FETCH_ERROR', err.response?.data?.message || err.message || 'Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDepartment = async (data: any) => {
    try {
      const response = await departmentApi.addDepartment(data);
      if (response.success !== false) {
        showSuccess('Success', 'Department added successfully');
        await fetchDepartments();
        setIsFormOpen(false);
      }
    } catch (err: any) {
      console.error('Failed to add department:', err);
      showError('ADD_ERROR', err.response?.data?.message || err.message || 'Failed to add department');
    }
  };

  const handleUpdateDepartment = async (data: any) => {
    if (!editingDepartment?.id) return;
    
    // Prevent updating system departments
    if (editingDepartment.is_default === true || editingDepartment.created_by === 'system') {
      showError('SYSTEM_DEPARTMENT', 'System default departments cannot be updated');
      return;
    }
    
    try {
      const response = await departmentApi.updateDepartment(editingDepartment.id, data);
      if (response.success !== false) {
        showSuccess('Success', 'Department updated successfully');
        await fetchDepartments();
        setIsFormOpen(false);
        setEditingDepartment(null);
      }
    } catch (err: any) {
      console.error('Failed to update department:', err);
      showError('UPDATE_ERROR', err.response?.data?.message || err.message || 'Failed to update department');
    }
  };

  const handleDeleteDepartment = async (id: string) => {
    // Find the department to check if it's a system department
    const departmentToDelete = departments.find(dept => dept.id === id);
    
    // Prevent deletion of system departments
    if (departmentToDelete?.is_default === true || departmentToDelete?.created_by === 'system') {
      showError('SYSTEM_DEPARTMENT', 'System default departments cannot be deleted');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this department? This action cannot be undone.')) {
      try {
        const response = await departmentApi.deleteDepartment(id);
        if (response.success !== false) {
          showSuccess('Success', 'Department deleted successfully');
          await fetchDepartments();
        }
      } catch (err: any) {
        console.error('Failed to delete department:', err);
        showError('DELETE_ERROR', err.response?.data?.message || err.message || 'Failed to delete department');
      }
    }
  };

  const handleEditDepartment = (dept: Department) => {
    // Prevent editing of system departments
    if (dept.is_default === true || dept.created_by === 'system') {
      showError('SYSTEM_DEPARTMENT', 'System default departments cannot be edited');
      return;
    }
    setEditingDepartment(dept);
    setIsFormOpen(true);
  };

  const handleAddNewDepartment = () => {
    setEditingDepartment(null);
    setIsFormOpen(true);
  };

  const filteredDepartments = departments.filter(dept =>
    dept.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dept.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Count custom departments (non-system)
  const customDepartmentsCount = departments.filter(dept => 
    dept.is_default !== true && dept.created_by !== 'system'
  ).length;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Department Management</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your organization departments</p>
      </div>

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search departments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button
          onClick={handleAddNewDepartment}
          className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Department
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4">
          <p className="text-sm text-blue-600 font-medium">Total Departments</p>
          <p className="text-2xl font-bold text-blue-900">{departments.length}</p>
        </div>
        <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4">
          <p className="text-sm text-green-600 font-medium">System Departments</p>
          <p className="text-2xl font-bold text-green-900">
            {departments.filter(dept => dept.is_default === true || dept.created_by === 'system').length}
          </p>
        </div>
        <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4">
          <p className="text-sm text-purple-600 font-medium">Custom Departments</p>
          <p className="text-2xl font-bold text-purple-900">{customDepartmentsCount}</p>
        </div>
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-4">
          <p className="text-sm text-orange-600 font-medium">Search Results</p>
          <p className="text-2xl font-bold text-orange-900">{filteredDepartments.length}</p>
        </div>
      </div>

      {/* Department List */}
      <DepartmentList
        departments={filteredDepartments}
        onEdit={handleEditDepartment}
        onDelete={handleDeleteDepartment}
        loading={loading}
      />

      {/* Department Form Modal */}
      <DepartmentForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingDepartment(null);
        }}
        onSubmit={editingDepartment ? handleUpdateDepartment : handleAddDepartment}
        initialData={editingDepartment}
        isLoading={loading}
      />

      <Popup
        isOpen={popup.isOpen}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        onClose={closePopup}
      />
    </div>
  );
}